import axios from 'axios';

import { VERSION_MANIFEST_FILENAME } from '@/constants/app-update.ts';

import { ApiError } from '@/lib/api-client.ts';

import { type VersionManifest, versionManifestSchema } from '@/schemas/version-manifest.schema.ts';

/**
 * Fetches the static version manifest (`version.json`) emitted at build time.
 *
 * Uses `axios` directly rather than the shared `api` client because the manifest is a same-origin static
 * asset, not an Eterno API, and must not receive a Cognito auth token. The request is cache-busted (query
 * param + no-cache header) so the CDN cannot return a stale manifest, and the response is validated before
 * use; on any mismatch we throw `ApiError(502)` and never return unvalidated data.
 */
export const fetchVersionManifest = async (): Promise<VersionManifest> => {
    const url = `${globalThis.location.origin}/${VERSION_MANIFEST_FILENAME}`;
    const response = await axios.get(url, {
        params: { t: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
    });

    const parsed = versionManifestSchema.safeParse(response.data);
    if (!parsed.success) {
        throw new ApiError(502, 'Invalid version manifest response');
    }

    return parsed.data;
};
